import axios from 'axios'

// Convert applicationServerKey to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

/**
 * Subscribe to push notifications
 * @returns {Promise<success>}
 */
export const subscribeUser = (user) => {
  if (!('serviceWorker' in navigator && 'Notification' in window && 'localStorage' in window)) {
    console.warn('Incompatible browser for push notification')
    return Promise.resolve(false)
  }

  // Check notification permission first
  if (Notification.permission === 'denied') {
    console.warn('Notification permission denied by user')
    return Promise.resolve(false)
  }

  // Request permission if not granted
  if (Notification.permission === 'default') {
    return Notification.requestPermission()
      .then(permission => {
        console.log('Notification permission:', permission)
        if (permission !== 'granted') {
          return false
        }
        return subscribeUser(user) // Retry after permission granted
      })
  }

  return navigator.serviceWorker.ready
    .then(registration => {
      if (!registration.pushManager) {
        // Maybe iOS on iPhone or iPad - should ask for adding to Home Screen
        console.log('PushManager not available, maybe should install app on iOS...')
        return
      }
      console.log('Service Worker ready, attempting push subscription...')
      // Use the PushManager to get the user's subscription to the push service
      return registration.pushManager.getSubscription()
        .then(subscription => {
          const applicationServerKey = urlBase64ToUint8Array(user.vapid_public_key)

          // If a subscription was found, return it
          let promise = Promise.resolve(subscription)
          if (subscription && subscription.options.applicationServerKey) {
            const currentKey = new Uint8Array(subscription.options.applicationServerKey)
            if (applicationServerKey.length === currentKey.length && currentKey.every((val, index) => val === applicationServerKey[index])) {
              console.log('Existing push subscription found')
              return subscription
            } else {
              console.log('Existing push subscription found but application key changed, unsubscribe old subscription...')
              promise = subscription.unsubscribe()
                .then(() => {
                  axios.delete('/api/pushsubs', {
                    data: subscription,
                  })
                })
                .then(() => {
                  localStorage.removeItem('service:push:' + user.id)
                  return subscription
                })
            }
          }
          return promise.then(() => {
            console.log('Creating new push subscription...  vapid_key=' + user.vapid_public_key)
            // Otherwise, subscribe the user (userVisibleOnly allows to specify that we don't plan to send notifications that don't have a visible effect for the user)
            return registration.pushManager
              .subscribe({
                userVisibleOnly: true,
                applicationServerKey,
              })
          })
        })
    })
    .then(subscription => {
      // Send the subscription details to the server
      console.log('Sending subscription to server...')
      return axios.post('/api/pushsubs', subscription)
        .then(() => {
          console.log('Push subscription successful')
          localStorage.setItem('service:push:' + user.id, JSON.stringify(subscription))
          return true
        })
        .catch(err => {
          console.error('Failed to save subscription to server:', err.response?.data || err.message)
          throw err
        })
    })
    .catch(err => {
      if (Notification.permission === 'denied') {
        console.warn('Permission for notifications was denied')
      } else {
        console.error('Unable to subscribe to push:', err)
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        })
      }
      localStorage.removeItem('service:push:' + user.id)
      return false
    })
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<success>}
 */
export const unsubscribeUser = (user) => {
  const subscription = localStorage.getItem('service:push:' + user.id)
  if (!subscription) return
  return axios.delete('/api/pushsubs', {
    data: JSON.parse(subscription),
  })
    .then(() => {
      localStorage.removeItem('service:push:' + user.id)
      return true
    })
    .catch(err => {
      console.error(err)
      return false
    })
}

// Promise when serviceWorker is initialized
let serviceWorkerPromise = Promise.resolve(false)

/*
 * Start service worker
 */
export default (vue) => {
  if (!('serviceWorker' in navigator && 'Notification' in window && 'localStorage' in window)) {
    console.warn('Service Worker, Notifications, or localStorage not supported')
    return
  }
  serviceWorkerPromise = new Promise((resolve) => {
    window.addEventListener('load', () => {
      console.log('Registering Service Worker...')
      // Install and register service worker
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope)

          // Wait for the service worker to be ready (installed + activated)
          return navigator.serviceWorker.ready
        })
        .then(registration => {
          console.log('ServiceWorker is ready and active')
          resolve(true)
        })
        .catch(err => {
          console.error('ServiceWorker registration or activation failed: ', err)
          resolve(false)
        })

      // Handler for messages coming from the service worker
      navigator.serviceWorker.addEventListener('message',
        ev => {
          const data = ev.data
          if (data.redirect && (data.redirect.startsWith('http://') || data.redirect.startsWith('https://'))) {
            if (data.redirect.startsWith(window.origin)) {
              vue.$router.push(data.redirect.substring(window.origin.length))
            } else if (data.redirect.startsWith('http://localhost')) {
              vue.$router.push(data.redirect.substring(data.redirect.indexOf('/', 10)))
            } else {
              vue.$router.redirect(data.redirect)
            }
          }
        })
    })
  })
}

/**
 * Check that notification push is available
 * @returns {Promise<bool>}
 */
export const isPushAvailable = () => {
  return serviceWorkerPromise
}

/**
 * Check if notification push are enabled
 * @returns {Promise<bool>}
 */
export const isPushEnabled = (user) => {
  if (!user) return false
  if (localStorage.getItem('service:push:' + user.id)) {
    return Promise.resolve(true)
  }
  return isPushAvailable()
    .then(res => {
      if (res) return subscribeUser(user)
      return false
    })
}

/**
 * Return the subscription json data, if notification are enabled
 * @returns {string}
 */
export const getSubJson = (user_id) => {
  return localStorage.getItem('service:push:' + user_id)
}
